import React from "react";
import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { FontSize, TextStyle } from "@tiptap/extension-text-style";

const EMPTY_DOCUMENT = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

const editorDocument = (document) => {
  let value = document;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return EMPTY_DOCUMENT;
    }
  }
  if (!value || !Object.keys(value).length) return EMPTY_DOCUMENT;
  const copy = { ...value };
  delete copy.version;
  return copy;
};

export default function SenderSignatureEditor({
  document,
  onChange,
  children,
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      TextStyle,
      FontSize,
      TextAlign.configure({
        types: ["heading", "paragraph"],
        alignments: ["left", "center"],
      }),
    ],
    content: editorDocument(document),
    onUpdate: ({ editor: updatedEditor }) =>
      onChange({ version: 1, ...updatedEditor.getJSON() }),
  });

  const toolbar = (label, command, icon) => (
    <Tooltip title={label}>
      <IconButton aria-label={label} sx={{ border: "none" }} onClick={command}>
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        sx={{ border: "1px solid", borderColor: "divider", p: 0.5 }}
      >
        {toolbar(
          "Bold",
          () => editor?.chain().focus().toggleBold().run(),
          <FormatBold />,
        )}
        {toolbar(
          "Italic",
          () => editor?.chain().focus().toggleItalic().run(),
          <FormatItalic />,
        )}
        {toolbar(
          "Underline",
          () => editor?.chain().focus().toggleUnderline().run(),
          <FormatUnderlined />,
        )}
        {toolbar(
          "Bulleted list",
          () => editor?.chain().focus().toggleBulletList().run(),
          <FormatListBulleted />,
        )}
        {toolbar(
          "Numbered list",
          () => editor?.chain().focus().toggleOrderedList().run(),
          <FormatListNumbered />,
        )}
        {toolbar(
          "Align left",
          () => editor?.chain().focus().setTextAlign("left").run(),
          <FormatAlignLeft />,
        )}
        {toolbar(
          "Align centre",
          () => editor?.chain().focus().setTextAlign("center").run(),
          <FormatAlignCenter />,
        )}
        <Select
          size="small"
          value=""
          displayEmpty
          sx={{ minWidth: 120, ml: 1 }}
          onChange={(event) =>
            editor?.chain().focus().setFontSize(event.target.value).run()
          }
        >
          <MenuItem value="" disabled>
            Font size
          </MenuItem>
          <MenuItem value="12px">Small</MenuItem>
          <MenuItem value="14px">Normal</MenuItem>
          <MenuItem value="16px">Large</MenuItem>
          <MenuItem value="20px">Heading</MenuItem>
        </Select>
      </Stack>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderTop: 0,
          bgcolor: "background.paper",
          p: 2,
          minHeight: 180,
          "& .ProseMirror": { minHeight: 100, outline: "none" },
        }}
      >
        <EditorContent editor={editor} />
        {children}
      </Box>
    </Box>
  );
}
